import type { components } from '@/api/client';
import type {
  MeasurementQualifierViewModel,
  MeasurementSourceViewModel,
  MeasurementType,
  MeasurementViewModel,
  MedicalHistoryViewModel,
} from '@/features/patient-record/types';
import { narrowEnum, nullableString } from '@/api/mappers/_shared';

type MedicalHistoryDto = components['schemas']['MedicalHistory'];
type MeasurementDto = components['schemas']['Measurement'];
type MeasurementQualifierDto = components['schemas']['MeasurementQualifier'];
type MeasurementSourceDto = components['schemas']['MeasurementSource'];
type PartialDateDto = components['schemas']['PartialDate'];

const KNOWN_MEASUREMENT_TYPE: ReadonlySet<MeasurementType> = new Set<MeasurementType>([
  'WEIGHT',
  'HEIGHT',
  'HEAD_CIRCUMFERENCE',
  'ARM_SPAN',
]);

export function mapMedicalHistory(
  dto: MedicalHistoryDto | null | undefined,
): MedicalHistoryViewModel {
  // BE returns an empty 200 body when the patient has no record yet —
  // http.ts surfaces that as `undefined`. Treat it the same as a record
  // whose fields are all blank.
  if (dto === undefined || dto === null) {
    return { indicationForReferral: null, medicalAndDevelopmentalHistory: null };
  }
  return {
    indicationForReferral: nullableString(dto.indicationForReferral),
    medicalAndDevelopmentalHistory: nullableString(
      dto.medicalAndDevelopmentalHistory,
    ),
  };
}

/**
 * Assemble an ISO `YYYY-MM-DD` string from the BE's partial-date object.
 * Missing components (day=0 or month=0) collapse to `01` so the table
 * can still sort/group by date. Use `datePartial` (round-tripped on the
 * VM) when fidelity matters — e.g. when sending PUT back to the BE.
 */
function assembleIsoDate(date: PartialDateDto): string {
  const year = String(date.year ?? 0).padStart(4, '0');
  const month = String(normalizeComponent(date.month) || 1).padStart(2, '0');
  const day = String(normalizeComponent(date.day) || 1).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeComponent(value: number | null | undefined): number {
  if (value === undefined || value === null) return 0;
  return Number.isFinite(value) ? value : 0;
}

function mapQualifier(dto: MeasurementQualifierDto): MeasurementQualifierViewModel {
  return {
    measurementType: narrowEnum<MeasurementType>(
      dto.measurementType,
      KNOWN_MEASUREMENT_TYPE,
      'WEIGHT',
    ),
    valueCm: nullableNumber(dto.valueCm),
    valueKg: nullableNumber(dto.valueKG),
    valueSD: nullableNumber(dto.valueSD),
    valuePercentile: nullableNumber(dto.valuePercentile),
  };
}

function nullableNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function mapSource(
  dto: MeasurementSourceDto | null | undefined,
): MeasurementSourceViewModel | null {
  if (dto === undefined || dto === null) return null;
  const type = nullableString(dto.type);
  const label = nullableString(dto.label);
  if (type === null && label === null) return null;
  return { type, label };
}

export function mapMeasurement(dto: MeasurementDto): MeasurementViewModel {
  const qualifiers = (dto.measurementQualifiers ?? []).map(mapQualifier);
  return {
    id: dto.id,
    dateMeasured: assembleIsoDate(dto.date),
    datePartial: {
      day: normalizeComponent(dto.date.day),
      month: normalizeComponent(dto.date.month),
      year: normalizeComponent(dto.date.year),
    },
    ageInMonths: nullableNumber(dto.ageInMonths),
    notes: nullableString(dto.notes),
    qualifiers,
    source: mapSource(dto.source),
  };
}

export function mapMeasurements(
  dtos: readonly MeasurementDto[] | null | undefined,
): MeasurementViewModel[] {
  if (dtos === undefined || dtos === null) return [];
  return dtos.map(mapMeasurement);
}
